import type { Sprite } from 'pixi.js';
import type { LuckyWheelPopupLayout } from '../../../../layouts/luckyWheel/LuckyWheelPopupLayout';
import { waitForMs } from '../../../utils/async-utils';

export class LuckyWheelLightAnimator {
  public readonly leftLights: Sprite[];

  public readonly rightLights: Sprite[];

  constructor(luckyWheelLayout: LuckyWheelPopupLayout) {
    const {
      light1Left,
      light2Left,
      light3Left,
      light4Left,
      light5Left,
      light6Left,
      light7Left,
      light8Left,
      light9Left,
      light1Right,
      light2Right,
      light3Right,
      light4Right,
      light5Right,
      light6Right,
      light7Right,
      light8Right,
      light9Right,
    } = luckyWheelLayout;

    this.leftLights = [
      light1Left,
      light2Left,
      light3Left,
      light4Left,
      light5Left,
      light6Left,
      light7Left,
      light8Left,
      light9Left,
    ];
    this.rightLights = [
      light1Right,
      light2Right,
      light3Right,
      light4Right,
      light5Right,
      light6Right,
      light7Right,
      light8Right,
      light9Right,
    ];

    this.leftLights.forEach((light) => {
      light.alpha = 0;
    });
    this.rightLights.forEach((light) => {
      light.alpha = 0;
    });
  }

  public animateIdle(): void {
    const animDuration = 2000;

    this.leftLights.forEach((light, index) => {
      this.resetLight(light);
      light.tweenTo({ alpha: [1, 0] }, animDuration, 'expOut', true, index % 2 === 0 ? 0 : animDuration * 0.5, -1);
    });

    this.rightLights.forEach((light, index) => {
      this.resetLight(light);
      light.tweenTo({ alpha: [1, 0] }, animDuration, 'expOut', true, index % 2 === 0 ? 0 : animDuration * 0.5, -1);
    });
  }

  public animateSpin(): void {
    const animDuration = 800;

    this.leftLights.forEach((light, index) => {
      this.resetLight(light);
      light
        .tweenTo({ alpha: [1, 0] }, animDuration, 'quadInOut', false, index * 150, -1)
        .repeatDelay(800)
        .start();
    });

    this.rightLights.forEach((light, index) => {
      this.resetLight(light);
      light
        .tweenTo({ alpha: [1, 0] }, animDuration, 'quadInOut', false, (this.rightLights.length - index) * 150, -1)
        .repeatDelay(800)
        .start();
    });
  }

  public async animateStop(wheelStopTotalDuration: number): Promise<void> {
    const lightsPerGroup = 3;

    await this.animateTurnOff();

    await waitForMs(250);

    const promises: Promise<void>[] = [];

    this.leftLights.forEach((light, index) => {
      this.resetLight(light);
      promises.push(
        light
          .tweenTo(
            { alpha: 1 },
            250,
            'cubicOut',
            true,
            (wheelStopTotalDuration / lightsPerGroup) * Math.floor(index / lightsPerGroup),
          )
          .asyncOnComplete(),
      );
    });

    this.rightLights.forEach((light, index) => {
      this.resetLight(light);
      promises.push(
        light
          .tweenTo(
            { alpha: 1 },
            250,
            'cubicOut',
            true,
            (wheelStopTotalDuration / lightsPerGroup) * Math.floor(index / lightsPerGroup),
          )
          .asyncOnComplete(),
      );
    });

    await Promise.all(promises);
  }

  public animateReward(): void {
    const animDuration = 250;
    const repeats = 8;

    this.leftLights.forEach((light) => {
      this.resetLight(light);
      light.tweenTo({ alpha: [0.5, 0] }, animDuration, 'sinInOut', true, 0, repeats);
    });

    this.rightLights.forEach((light) => {
      this.resetLight(light);
      light.tweenTo({ alpha: [0.5, 0] }, animDuration, 'sinInOut', true, 0, repeats);
    });
  }

  public async animateTurnOff(): Promise<void> {
    const animDuration = 300;
    const promises: Promise<void>[] = [];

    await waitForMs(100);

    this.leftLights.forEach((light) => {
      promises.push(light.tweenTo({ alpha: 0 }, animDuration, 'sinInOut', true, 0).asyncOnComplete());
    });

    this.rightLights.forEach((light) => {
      promises.push(light.tweenTo({ alpha: 0 }, animDuration, 'sinInOut', true, 0).asyncOnComplete());
    });

    await Promise.all(promises);

    this.leftLights.forEach((light) => {
      this.resetLight(light);
    });

    this.rightLights.forEach((light) => {
      this.resetLight(light);
    });
  }

  private resetLight(light: Sprite): void {
    light.removeTweens();
    light.props({
      alpha: 0,
    });
  }
}